import clsx from 'clsx';
import React, { useContext } from 'react';
import { get } from 'lodash';
import Container from '../components/atoms/Container/Container';
import Cart from '../components/organisms/Cart/Cart';
import CartSummary from '../components/molecules/CartSummary/CartSummary';
import Layout from '../components/organisms/Layout/Layout';
import Seo from '../components/organisms/Seo/Seo';
import CartContext from '../context/CartProvider';
import recommend from '@algolia/recommend';
import { useRecommendations } from '@algolia/recommend-react';
import { Box, Spinner } from '@chakra-ui/react';
import * as pcStyles from './confirm-order.module.scss';
import * as mpsStyles from './confirm-order.mps.module.scss';
import { storeStyle, isMadPawsStore } from '../common/util';
import FrequentProducts from '../components/molecules/FrequentsProduct/FrequentProducts';
import AutoShip from '../components/molecules/AutoShip/AutoShip';
import Link from '../components/atoms/Link/Link';
import useSegment from 'hooks/useSegment';

const recommendClient = recommend(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_SEARCH_API_KEY
);

const indexName = process.env.GATSBY_ALGOLIA_INDEX_NAME;
const styles = storeStyle({ mpsStyles, pcStyles });
const ConfirmOrderPage = () => {
  const cartCtx = useContext(CartContext);
  const cartFetched = get(cartCtx, 'state.cartFetched', false);
  const { track } = useSegment();
  const cart = get(cartCtx, 'state.cart', {});
  const numberItems = get(cartCtx, 'state.cart.numberItems');
  const checkout = get(cartCtx, 'state.checkout', {});
  //Need to remove skus INS, REPLY, and SIGREQ from recommendations
  const { recommendations } = useRecommendations({
    model: 'related-products',
    recommendClient,
    indexName,
    objectIDs:
      cart.lineItems?.physical_items
        ?.filter(item => !['INS', 'REPLY', 'SIGREQ'].includes(item.sku))
        .map(item => String(item.product_id)) ?? [],
    maxRecommendations: 3
  });

  const productTrackData = () => {
    return checkout?.cart?.line_items?.physical_items?.map(item => {
      const product = cart?.productInfo[item.product_id];
      const isMedication = product?.custom_fields?.find(
        field => field.name === 'Product Type' && field.value === 'Medication'
      );
      return {
        name: item.name,
        sku: item.sku,
        quantity: item.quantity,
        product_type: !!isMedication ? 'Medication' : 'OTC',
        price: item.list_price,
        variant: item.variant_id,
        image_url: item.image_url
      };
    });
  };

  const trackCheckout = async () => {
    // add delay to ensure user is identified
    console.log('trackCheckout1');

    await track('Checkout Started', {
      affiliation: isMadPawsStore() ? 'MPS' : 'PC',
      currency: cart?.currency?.code ?? 'AUD',
      cartId: cart?.cartId,

      coupon: checkout?.coupons?.map(coupon => coupon.code).join(','),
      discount: checkout?.cart?.discount_amount ?? 0,
      products: productTrackData()
    });
  };

  return (
    <Layout className={`${styles.root}`} slim>
      {/* <input type="text" ref={searchRef} /> */}
      <Seo title="Confirm your order" />
      <Container size="large">
        {!cartFetched && (
          <div className={styles.bcConfirmOrderContainerEmpty}>
            <div className={styles.bcCartTitle}>
              <h1 className="h3 text-left">Cart</h1>
            </div>
            <div className={styles.bcCartEmpty}>
              <div className="text-center">
                <Spinner size={'xl'} color="teal" />
                <p className="subtitle text-center" style={{ paddingTop: '40px' }}>
                  Loading cart...
                </p>
              </div>
            </div>
          </div>
        )}
        {cartFetched && !numberItems && (
          <div className={styles.bcConfirmOrderContainer}>
            <div className={styles.bcCartTitle}>
              <h1 className="h3 text-left">Cart</h1>
              <Link url="/" className={styles.continueShopping}>
                continue shopping
              </Link>
            </div>
            <div className={styles.bcCartEmpty}>
              <div className="text-center">
                <p className="subtitle text-center">Your cart is empty.</p>
                <Link url="/">Take a look around.</Link>
              </div>
            </div>
          </div>
        )}
        {cartFetched && !!numberItems && (
          <div name="section-cart" id="section-cart">
            <Box className={clsx('grid-7030', styles.bcConfirmOrderContainer)} gap={8}>
              <Box>
                <>
                  <div className={styles.bcCartTitle}>
                    <h1 className="h3 text-left">My Cart</h1>
                  </div>
                  <div>
                    {cart?.frequencyOptions && cart?.frequencyOptions.length > 0 && <AutoShip />}
                    <Cart cartType="full" />
                  </div>
                </>
              </Box>
              <CartSummary cartType="full" trackCheckout={trackCheckout} />
            </Box>
          </div>
        )}

        {cartFetched && !!numberItems && recommendations?.length > 0 && (
          <Box mt={20} pb={105}>
            <Box
              as="h3"
              fontSize={{
                base: 22,
                md: 32
              }}
              pb={8}
              className={styles.recommend}
            >
              Recommended for you
            </Box>
            <FrequentProducts products={recommendations} />
          </Box>
        )}
      </Container>
    </Layout>
  );
};

export default ConfirmOrderPage;
